import { useState } from 'react';
import { Title, useNotify } from 'react-admin';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import LinearProgress from '@mui/material/LinearProgress';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import StorefrontIcon from '@mui/icons-material/Storefront';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PeopleIcon from '@mui/icons-material/People';

import {
  ADMIN_GET_MERCHANT_CATALOG_PLANS,
  ADMIN_GET_MERCHANT_CATALOG_PLAN_STATS,
} from './merchant-catalog-plan.queries';
import {
  MerchantCatalogPlan,
  MerchantCatalogPlanStats,
  PLAN_LABELS,
  PLAN_COLORS,
  STATUS_LABELS,
  STATUS_COLORS,
  formatDate,
  trialDaysRemaining,
} from './merchant-catalog-plan.types';

// ─── Tab Config ──────────────────────────────────────────────────────────────

const TABS = [
  { label: 'Tous', value: '' },
  { label: 'Actifs', value: 'ACTIVE' },
  { label: 'En essai', value: 'TRIALING' },
  { label: 'Annulés', value: 'CANCELLED' },
  { label: 'Expirés', value: 'EXPIRED' },
];

// ─── StatsCard ────────────────────────────────────────────────────────────────

interface StatsCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
  loading?: boolean;
}

function StatsCard({ title, value, icon, color = '#1976d2', loading }: StatsCardProps) {
  return (
    <Card>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ p: 1.5, borderRadius: 2, background: `${color}18`, color }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {loading ? <Skeleton width={60} /> : value}
          </Typography>
          <Typography variant="body2" color="text.secondary">{title}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── Main List ────────────────────────────────────────────────────────────────

export function MerchantCatalogPlanList() {
  const notify = useNotify();
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [tabValue, setTabValue] = useState('');
  const [planCodeFilter, setPlanCodeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // ── Queries ─────────────────────────────────────────────────────────────────

  const { data: statsData, loading: statsLoading } = useQuery<{
    adminMerchantCatalogPlanStats: MerchantCatalogPlanStats;
  }>(ADMIN_GET_MERCHANT_CATALOG_PLAN_STATS);

  const { data, loading, error, refetch } = useQuery<{
    adminMerchantCatalogPlans: {
      total: number;
      page: number;
      pageSize: number;
      items: MerchantCatalogPlan[];
    };
  }>(ADMIN_GET_MERCHANT_CATALOG_PLANS, {
    variables: {
      page: page + 1,
      pageSize,
      status: tabValue || undefined,
      planCode: planCodeFilter || undefined,
      search: search || undefined,
    },
    fetchPolicy: 'cache-and-network',
    onError: (err) => notify(`Erreur: ${err.message}`, { type: 'error' }),
  });

  const plans = data?.adminMerchantCatalogPlans?.items ?? [];
  const total = data?.adminMerchantCatalogPlans?.total ?? 0;
  const stats = statsData?.adminMerchantCatalogPlanStats;

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setSearch(searchInput);
      setPage(0);
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
    setPage(0);
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }}>
      <Title title="Catalogues Marchands" />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Catalogues Marchands</Typography>
        <Tooltip title="Rafraîchir">
          <IconButton onClick={() => refetch()} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total plans"
            value={stats?.total ?? 0}
            icon={<StorefrontIcon />}
            color="#1976d2"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="En essai actif"
            value={stats?.activeTrials ?? 0}
            icon={<HourglassEmptyIcon />}
            color="#ed6c02"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Plans Premium"
            value={stats?.byPlanCode?.CATALOGUE_PREMIUM ?? 0}
            icon={<WorkspacePremiumIcon />}
            color="#2e7d32"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Taux conversion essai"
            value={stats ? `${Math.round((stats.trialConversionRate ?? 0) * 100)}%` : '—'}
            icon={<PeopleIcon />}
            color="#9c27b0"
            loading={statsLoading}
          />
        </Grid>
      </Grid>

      {/* Repartition bar */}
      {stats && !statsLoading && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Répartition par plan
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {['CATALOGUE_FREE', 'CATALOGUE_PRO', 'CATALOGUE_PREMIUM'].map((code) => {
              const count = stats.byPlanCode?.[code] ?? 0;
              const pct = stats.total > 0 ? Math.round((count / stats.total) * 100) : 0;
              return (
                <Box key={code} sx={{ flex: 1, minWidth: 100 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption">{PLAN_LABELS[code]}</Typography>
                    <Typography variant="caption" fontWeight={700}>{count} ({pct}%)</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    color={PLAN_COLORS[code] as any}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      {/* Tabs */}
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {TABS.map((t) => (
          <Tab
            key={t.value}
            label={`${t.label}${t.value && stats?.byStatus?.[t.value] != null ? ` (${stats.byStatus[t.value]})` : ''}`}
            value={t.value}
          />
        ))}
      </Tabs>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Rechercher (carte, marchand, email…)"
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={handleSearch}
          sx={{ minWidth: 280 }}
          placeholder="Appuyez sur Entrée"
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Plan</InputLabel>
          <Select
            value={planCodeFilter}
            label="Plan"
            onChange={(e) => { setPlanCodeFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="">Tous</MenuItem>
            <MenuItem value="CATALOGUE_FREE">Gratuit</MenuItem>
            <MenuItem value="CATALOGUE_PRO">Pro</MenuItem>
            <MenuItem value="CATALOGUE_PREMIUM">Premium</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Error */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}

      {/* Loading bar */}
      {loading && <LinearProgress sx={{ mb: 1 }} />}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 700 }}>Boutique</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Marchand</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Statut</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Essai</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Parrainages</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Créé le</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && plans.length === 0 ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 8 }).map((_, j) => (
                    <TableCell key={j}><Skeleton /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : plans.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Aucun plan trouvé
                </TableCell>
              </TableRow>
            ) : (
              plans.map((plan) => {
                const daysLeft = trialDaysRemaining(plan.trialEndsAt);
                const merchant = plan.digitalCard;
                const user = merchant?.user;

                return (
                  <TableRow
                    key={plan.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/merchant-catalog-plans/${plan.id}`)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {merchant?.displayName ?? '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {merchant?.slug ?? plan.digitalCardId.slice(0, 8)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email : '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user?.email ?? user?.phoneNumber ?? ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={PLAN_LABELS[plan.planCode] ?? plan.planCode}
                        color={PLAN_COLORS[plan.planCode] ?? 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABELS[plan.status] ?? plan.status}
                        color={STATUS_COLORS[plan.status] ?? 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {plan.isTrialActive ? (
                        <Box>
                          <Typography variant="caption" color="warning.main" fontWeight={600}>
                            {daysLeft}j restants
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            fin : {formatDate(plan.trialEndsAt)}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {plan.referralsEarned}/5
                      </Typography>
                      {plan.referralBonusProducts > 0 && (
                        <Typography variant="caption" color="success.main">
                          +{plan.referralBonusProducts} articles
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{formatDate(plan.createdAt)}</Typography>
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Voir le détail">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/merchant-catalog-plans/${plan.id}`)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Lignes par page"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
      />
    </Box>
  );
}
